import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { AdminPlaceRow } from '@/admin-area/place/AdminPlaceRow';
import { placeFromPath } from '@/admin-area/place/place-from-path';
import { ContextSelector } from '../ContextSelector';

/**
 * Decision 6: the scope row, visible at every width.
 *
 * WHAT IT FIXES. The context selector was in the header from `lg:` up and moved into the
 * navigation drawer's FOOTER below it. On a phone, the organization every write lands in was
 * therefore invisible until you opened the drawer and scrolled to the bottom of it. The standard
 * requires a scope selector to be "visible at all times" and to "persist across screens", and half
 * of the platform's widths did not satisfy the first half of that sentence.
 *
 * WHY A ROW AND NOT A HEADER SLOT. The standard's own § Application structure says scope selectors
 * "live in the header" on desktop and become "scrollable chips beneath the header" on a phone —
 * two structures for one thing, which is how the desktop-only implementation happened. One
 * structure at every width is the same guarantee with one less place to get wrong, and the row
 * SCROLLS rather than wrapping or hiding, so a third and fourth scope can be added without the
 * header being redesigned again. That divergence is recorded in the standard itself, in the change
 * that ships this.
 *
 * WHY IT IS A LIST OF ONE TODAY. The organization is the only scope the platform has. Finance's
 * year selector is a candidate — it applies to two of its four views, not to every screen of the
 * module, which is the standard's own test for what belongs here — and it is deliberately left
 * where it is until that changes. Adding a scope means adding a child to this element, and nothing
 * else.
 */
export function ScopeBar() {
  const { t } = useTranslation('shell');
  const { pathname } = useLocation();

  // Inside the administration area the slot shows the place being administered instead
  // (decision 60). The active organization is not changed, and it is back as soon as the address
  // leaves the area.
  const located = placeFromPath(pathname);
  if (located !== null) {
    return <AdminPlaceRow place={located.place} />;
  }

  return (
    <div className="shell-scope" aria-label={t('scope.label')} role="group">
      <span className="shell-scope-label" aria-hidden="true">
        {t('scope.label')}
      </span>
      <ContextSelector />
    </div>
  );
}
